/**
 * Session comparison alignment algorithm.
 *
 * Aligns two action timelines using LCS (longest common subsequence)
 * on tool names, then classifies matches as exact/similar based on args.
 */

export interface AlignableAction {
  seq: number;
  tool: string;
  args: Record<string, unknown>;
}

export type MatchType = 'exact' | 'similar' | 'added' | 'removed';

export interface AlignmentEntry {
  seqA: number | null;
  seqB: number | null;
  match: MatchType;
}

/** Deep-equal check for args objects. */
function argsEqual(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Align two action sequences using LCS on tool name.
 *
 * 1. Compute LCS of tool names between actionsA and actionsB.
 * 2. Matched pairs: exact if args identical, similar otherwise.
 * 3. Unmatched in A → removed, unmatched in B → added.
 */
export function alignActions(
  actionsA: AlignableAction[],
  actionsB: AlignableAction[],
): AlignmentEntry[] {
  const m = actionsA.length;
  const n = actionsB.length;

  // Build LCS table on tool name match
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (actionsA[i - 1].tool === actionsB[j - 1].tool) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to find matched pairs
  const matched: Array<[number, number]> = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (actionsA[i - 1].tool === actionsB[j - 1].tool) {
      matched.push([i - 1, j - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }
  matched.reverse();

  // Build alignment from matched pairs + unmatched items
  const result: AlignmentEntry[] = [];
  let ai = 0;
  let bi = 0;

  for (const [matchA, matchB] of matched) {
    // Emit unmatched items before this match
    while (ai < matchA) {
      result.push({ seqA: actionsA[ai].seq, seqB: null, match: 'removed' });
      ai++;
    }
    while (bi < matchB) {
      result.push({ seqA: null, seqB: actionsB[bi].seq, match: 'added' });
      bi++;
    }

    // Emit the matched pair
    const a = actionsA[matchA];
    const b = actionsB[matchB];
    result.push({
      seqA: a.seq,
      seqB: b.seq,
      match: argsEqual(a.args, b.args) ? 'exact' : 'similar',
    });

    ai = matchA + 1;
    bi = matchB + 1;
  }

  // Remaining unmatched items
  while (ai < m) {
    result.push({ seqA: actionsA[ai].seq, seqB: null, match: 'removed' });
    ai++;
  }
  while (bi < n) {
    result.push({ seqA: null, seqB: actionsB[bi].seq, match: 'added' });
    bi++;
  }

  return result;
}
